import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, X, MapPin, Star, Pencil, Power } from "lucide-react";
import { useBranches, useCreateBranch, useUpdateBranch, BranchRecord, CreateBranchPayload } from "@/api/inventory";
import { getApiErrorMessage } from "@/api/client";

/**
 * Sucursales (multi-sucursal): cada organización arranca con exactamente una
 * sucursal "Principal" (creada automáticamente en la migración); desde aquí
 * se agregan sucursales adicionales y se editan las existentes. La sucursal
 * principal no se puede desactivar (regla del backend, ver `CANNOT_DEACTIVATE_MAIN_BRANCH`)
 * porque varios flujos (creación de productos, lotes, ajustes sin sucursal
 * explícita) dependen de que siempre exista una.
 */
export default function BranchesPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<BranchRecord | null>(null);
  const { data: branches, isLoading } = useBranches();
  const updateBranch = useUpdateBranch();

  const handleToggleActive = async (branch: BranchRecord) => {
    if (branch.isMain && branch.isActive) {
      toast.error("La sucursal principal no se puede desactivar.");
      return;
    }
    const action = branch.isActive ? "desactivar" : "reactivar";
    if (!window.confirm(`¿Seguro que quieres ${action} "${branch.name}"?`)) return;
    try {
      await updateBranch.mutateAsync({ id: branch.id, isActive: !branch.isActive });
      toast.success(`Sucursal ${branch.isActive ? "desactivada" : "reactivada"}.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Sucursales</h1>
          <p className="text-sm text-gray-500">Administra las sucursales de tu negocio. Cada una lleva su propio inventario, turnos de caja y ventas.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1.5" /> Nueva sucursal
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Nombre</th>
              <th className="px-4 py-2.5">Dirección</th>
              <th className="px-4 py-2.5">Teléfono</th>
              <th className="px-4 py-2.5">Estado</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {branches?.map((b) => (
              <tr key={b.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-1.5 font-medium text-gray-900">
                    <MapPin size={14} className="text-gray-400" /> {b.name}
                    {b.isMain && (
                      <span className="ml-1 flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                        <Star size={9} className="fill-amber-500 text-amber-500" /> Principal
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-gray-600">{b.address ?? "—"}</td>
                <td className="px-4 py-2.5 text-gray-600">{b.phone ?? "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${b.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {b.isActive ? "Activa" : "Inactiva"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button type="button" className="mr-3 text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setEditing(b)}>
                    <Pencil size={13} className="mr-1 inline" /> Editar
                  </button>
                  {!b.isMain && (
                    <button
                      type="button"
                      className={`text-xs font-medium ${b.isActive ? "text-red-600 hover:text-red-800" : "text-green-600 hover:text-green-800"}`}
                      onClick={() => handleToggleActive(b)}
                    >
                      <Power size={13} className="mr-1 inline" /> {b.isActive ? "Desactivar" : "Reactivar"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {branches?.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  No hay sucursales registradas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <BranchModal onClose={() => setShowCreate(false)} />}
      {editing && <BranchModal branch={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function BranchModal({ branch, onClose }: { branch?: BranchRecord; onClose: () => void }) {
  const createBranch = useCreateBranch();
  const updateBranch = useUpdateBranch();
  const isEditing = !!branch;

  const [form, setForm] = useState<CreateBranchPayload>({
    name: branch?.name ?? "",
    address: branch?.address ?? "",
    phone: branch?.phone ?? "",
  });

  useEffect(() => {
    if (branch) setForm({ name: branch.name, address: branch.address ?? "", phone: branch.phone ?? "" });
  }, [branch]);

  const isPending = createBranch.isPending || updateBranch.isPending;
  const isValid = form.name.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("El nombre de la sucursal es obligatorio.");
      return;
    }
    const payload = { name: form.name.trim(), address: form.address?.trim() || undefined, phone: form.phone?.trim() || undefined };
    try {
      if (isEditing) {
        await updateBranch.mutateAsync({ id: branch.id, ...payload });
        toast.success("Sucursal actualizada.");
      } else {
        await createBranch.mutateAsync(payload);
        toast.success("Sucursal creada.");
      }
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">{isEditing ? "Editar sucursal" : "Nueva sucursal"}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre</label>
            <input className="input-field mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej: Sucursal Norte" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Dirección (opcional)</label>
            <input className="input-field mt-1" value={form.address ?? ""} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Teléfono (opcional)</label>
            <input className="input-field mt-1" value={form.phone ?? ""} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={isPending} onClick={handleSubmit}>
            {isPending ? "Guardando..." : isEditing ? "Guardar cambios" : "Crear sucursal"}
          </button>
        </div>
      </div>
    </div>
  );
}
